<template>
  <material-card
    ref="salesTeamPieRef"
    color="primary"
    full-header
  >
    <template #heading>
      <div class="pl-4 pr-4 pt-1 pb-1 white--text">
        <div class="text-h3 font-weight-light">
          <v-col>
            <v-row>
              Team Invoice Aging
              <v-spacer />
              <v-btn
                color="white"
                icon
                small
                @click="salesPieButtonClicked"
              >
                <v-icon>
                  mdi-axis-arrow
                </v-icon>
              </v-btn>
            </v-row>
          </v-col>
        </div>
      </div>
    </template>
    <v-row
      v-if="items.length == 0"
      style="align-items: center; justify-content: center;"
    >
      <v-card
        elevation="2"
        class="mb-10 mt-10"
        outlined
      >
        <v-card-title class="text-h3">
          No Outstanding Invoices
        </v-card-title>
      </v-card>
    </v-row>
    <v-row
      v-if="items.length > 0"
      style="align-items: center; justify-content: center;"
    >
      <v-checkbox
        v-if="noneCheck"
        v-model="allChecks"
        label="All"
        :indeterminate="allChecksIntermediate"
        @click="onAllClicked"
      />
      <v-checkbox
        v-if="noneCheck"
        v-model="lateDays"
        value="none"
      />
      <v-checkbox
        v-model="lateDays"
        class="pl-8"
        label="Within 10 days"
        value="within10days"
      />

      <v-checkbox
        v-model="lateDays"
        class="pl-8"
        label="31-60 Days"
        value="days31_60"
      />
      <v-checkbox
        v-model="lateDays"
        class="pl-8"
        label="61-90 days"
        value="days61_90"
      />
      <v-checkbox
        v-model="lateDays"
        class="pl-8"
        label="91-120 days"
        value="days91_120"
      />
      <v-checkbox
        v-model="lateDays"
        class="pl-8"
        label="Over 120 days"
        value="daysOver120"
      />
    </v-row>
    <v-row
      v-if="items.length > 0"
      align="center"
      justify="center"
      class="mt-10"
    >
      <v-col cols="9">
        <v-row>
          <v-range-slider
            v-model="range"
            step="1000"
            :max="max"
            :min="min"
            hide-details
            thumb-label="always"
            thumb-size="50"
            class="align-center"
            @end="rangeSliderEnd"
            @click="rangeSliderEnd"
          >
            <template v-slot:thumb-label="props">
              {{ formatCurrency(props.value) }}
            </template>
          </v-range-slider>
        </v-row>
        <v-row>
          <div class="primary--text mr-5">
            {{ formatCurrency(range[0]) }}
          </div>
          <v-spacer />
          <div class="primary--text ml-5">
            {{ formatCurrency(range[1]) }}
          </div>
        </v-row>
      </v-col>
    </v-row>
    <v-row>
      <v-col
        cols="12"
        sm="12"
        md="12"
        lg="3"
        xl="3"
      >
        <v-fade-transition>
          <v-card
            v-if="showTotals"
            color="white secondary--text"
            style="margin-left:30px;margin-top:50px;"
          >
            <v-toolbar
              dense
              color="secondary"
              dark
            >
              <v-toolbar-title>
                Invoices Totals
              </v-toolbar-title>
              <v-spacer />
              <v-icon>mdi-calendar-clock</v-icon>
            </v-toolbar>
            <v-container fluid>
              <v-row dense>
                <v-card-title class="text-body-1">
                  Number of Invoices:
                  <strong class="ml-2">
                    {{ sumField(allCompanyInvoices, "numberOfInvoices") }}
                  </strong>
                </v-card-title>
                <v-card-title class="text-body-1">
                  Amount of Invoices:
                  <strong class="ml-2">
                    {{ formatCurrency(sumSelectedInvoices()) }}
                  </strong>
                </v-card-title>
              </v-row>
            </v-container>
          </v-card>
        </v-fade-transition>
      </v-col>
      <v-col
        cols="12"
        sm="12"
        md="12"
        lg="9"
        xl="9"
      >
        <apexchart
          ref="apexChartRef"
          class="ml-3 pb-10"
          type="donut"
          :height="pHeight"
          :options="pieData.chartOptions"
          :series="pieData.series"
          @updated="salesPieUpdated"
          @dataPointSelection="pieClicked"
        />
      </v-col>
    </v-row>
  </material-card>
</template>
<script>
  import { get } from 'vuex-pathify'
  import _ from 'lodash'

  import { allInvoicesPieData } from '@/util/chartdata.js'
  import {
    formatDate,
    formatNumber,
    formatCurrency,
    formatPercentage,
    formatCurrencyBasic,
  } from '@/util/formatters.js'

  import MaterialCard from '@/components/MaterialCard.vue'

  export default {
    name: 'AgingChartView',
    components: {
      MaterialCard,
    },
    props: {
      items: Array,
      pieHeight: {
        type: Number,
        default: 700,
      },
    },
    data: () => ({
      age: null,
      min: 0,
      max: 1000,
      noneCheck: false,
      showTotals: false,
      transposedItemData: [],
      pHeight: 700,
      pieInputData: [],
      pieData: { chartOptions: {}, series: [] },
      allCompanyInvoices: [],
      range: [0, 1000],
      lateDaysEmpty: ['none'],
      lateDays: [
        'none',
        'days31_60',
        'days61_90',
        'days91_120',
        'daysOver120',
      ],
      lateDaysAll: [
        'none',
        'within10days',
        'days31_60',
        'days61_90',
        'days91_120',
        'daysOver120',
      ],
    }),
    computed: {
      ...get('user', ['dark']),
    },
    watch: {
      items () {
        if (this.items.length > 0) {
          this.transposedItemData = this.createPieData(this.items)
          console.log('Items Count', this.transposedItemData.length)
          const fd = this.filterResults(this.transposedItemData)
          const cd = this.combineResults(fd)
          this.min = _.floor(Math.min(...cd.map(item => item.totalBalance)))
          if (this.min === Infinity) {
            this.min = 0
          }
          this.max = _.round(Math.max(...cd.map(item => item.totalBalance)))
          if (this.max === -Infinity) {
            this.max = 0
          }
          this.range = [this.min, this.max]
          this.pieInputData = cd
          this.allCompanyInvoices = cd
          this.pieData = allInvoicesPieData(cd, this.getTheme())
        }
      },
      lateDays () {
        if (this.lateDaysAll.every(arItem => this.lateDays.includes(arItem))) {
          this.allChecks = true
          this.allChecksIntermediate = false
        } else if (this.lateDays === this.lateDaysEmpty) {
          this.allChecks = false
          this.allChecksIntermediate = false
        } else {
          this.allChecks = false
          this.allChecksIntermediate = true
        }
        const fd = this.filterResults(this.transposedItemData)
        const clearedResults = this.clearResults(fd)
        const cd = this.combineResults(clearedResults)
        this.min = _.floor(Math.min(...cd.map(item => item.totalBalance)))
        if (this.min === Infinity) {
          this.min = 0
        }
        this.max = _.round(Math.max(...cd.map(item => item.totalBalance)))
        if (this.max === -Infinity) {
          this.max = 0
        }
        this.range = [this.min, this.max]
        this.pieInputData = cd
        this.allCompanyInvoices = cd
        this.pieData = allInvoicesPieData(cd, this.getTheme())
      },
    },
    mounted () {
      this.pHeight = this.pieHeight
    },
    methods: {
      salesPieButtonClicked () {
        const g = this.$refs.salesTeamPieRef
        if (this.toggleMaxArea(g)) {
          this.pHeight = (window.innerHeight / 4) * 3
        } else {
          this.pHeight = this.pieHeight
        }
      },
      async salesPieUpdated (chartContext, config) {
        this.selectedCompany = ''
        this.$refs.apexChartRef.refresh()
        this.showTotals = true
      },
      companyInvoiceDetailsButtonClicked () {
        const g = this.$refs.companyInvoiceDetails
        if (this.toggleMaxArea(g)) {
          this.pHeight = (window.innerHeight / 4) * 3
        } else {
          this.pHeight = this.pieHeight
        }
      },
      createPieData (originalData) {
        const pd = originalData.map(item => {
          return {
            within10days: item.within10days,
            days31_60: item.days31_60,
            days61_90: item.days61_90,
            days91_120: item.days91_120,
            daysOver120: item.daysOver120,
            companyName: item.companyName,
            invoiceAmount: item.invoiceAmount,
            invoiceBalance: item.invoiceBalance,
            invoiceNumber: item.invoiceNumber,
            invoiceAge: item.age,
            invoiceDate: item.invoiceDate,
            companyId: item.companyId,
            user: `${item.userName} (${item.userID})`,
            userID: item.userID,
            userName: item.userName,

            numberOfInvoices: 1,
            totalAmount: item.invoiceAmount,
            totalBalance: item.invoiceBalance,
            totalWithin10days: item.within10days,
            totalDays31_60: item.days31_60,
            totalDays61_90: item.days61_90,
            totalDays91_120: item.days91_120,
            totalDaysOver120: item.daysOver120,

          }
        })

        return pd
      },
      filterResults (originalData) {
        this.fireClearSelectedCompany()
        const filtered = originalData.filter(item => {
          if (item.invoiceBalance > 0) {
            if (item.within10days > 0 && this.lateDays.includes('within10days')) {
              return true
            }
            if (item.days31_60 > 0 && this.lateDays.includes('days31_60')) {
              return true
            }
            if (item.days61_90 > 0 && this.lateDays.includes('days61_90')) {
              return true
            }
            if (item.days91_120 > 0 && this.lateDays.includes('days91_120')) {
              return true
            }
            if (item.daysOver120 > 0 && this.lateDays.includes('daysOver120')) {
              return true
            }
            return false
          }
          return false
          // if (
          //   item.invoiceBalance > 0 &&
          //   ((item.within10days > 0 &&
          //     this.lateDays.includes('within10days')) ||
          //     (item.days31_60 > 0 &&
          //       this.lateDays.includes('days31_60')) ||
          //     (item.days61_90 > 0 &&
          //       this.lateDays.includes('days61_90')) ||
          //     (item.days91_120 > 0 &&
          //       this.lateDays.includes('days91_120')) ||
          //     (item.daysOver120 > 0 &&
          //       this.lateDays.includes('daysOver120')))
          // ) {
          //   return true
          // }
          // return false
        })
        return filtered.sort((a, b) => {
          if (a.companyName > b.companyName) return 1
          else if (a.companyName < b.companyName) return -1
          else return 0
        })
      },
      fireSelectedCompanyChanged (companyInfo) {
        this.$emit('selectedCompanyChanged', companyInfo)
      },
      fireClearSelectedCompany () {
        this.$emit('unselectedCompany')
      },
      filterRange (originalData) {
        // Mulitplying * 1 to prevent coercion to strings
        const low = this.range[0].toFixed(2) * 1
        const high = this.range[1].toFixed(2) * 1
        const fd = originalData.filter(item => {
          const ib = Math.floor(item.totalBalance.toFixed(2) * 1)
          if (ib >= low && ib <= high) {
            return true
          }
          return false
        })
        return fd
      },
      clearResults (results) {
        return _.forEach(results, item => {
          item.numberOfInvoices = 1
          item.totalAmount = item.invoiceAmount
          item.totalBalance = item.invoiceBalance
          item.totalWithin10days = item.within10days
          item.totalDays31_60 = item.days31_60
          item.totalDays61_90 = item.days61_90
          item.totalDays91_120 = item.days91_120
          item.totalDaysOver120 = item.daysOver120
        })
      },
      combineResults (originalData) {
        console.log('Count', originalData.length)
        // first create an dictionary for the results
        var results = new Map()

        // Sort the originalData array
        const sorted = originalData.sort((a, b) => {
          if (a.companyName > b.companyName) return 1
          else if (a.companyName < b.companyName) return -1
          else return 0
        })

        // Next iterate over the original data.  If the company is NOT in
        // the results array add it, if it is, augment it's stats
        _.forEach(sorted, (item) => {
          var co = results.get(item.companyName)
          if (co === undefined) {
            co = item
          } else {
            co.totalAmount += item.totalAmount
            co.totalBalance += item.totalBalance
            co.totalWithin10days += item.totalWithin10days
            co.totalDays31_60 += item.totalDays31_60
            co.totalDays61_90 += item.totalDays61_90
            co.totalDays91_120 += item.totalDays91_120
            co.totalDaysOver120 += item.totalDaysOver120
            co.numberOfInvoices += 1
          }
          results.set(item.companyName, co)
        })

        return Array.from(results.values())
      },
      combineResults_old (originalData) {
        const merged = _.uniqWith(originalData, (pre, cur) => {
          if (pre.companyName === cur.companyName) {
            cur.totalAmount += pre.totalAmount
            cur.totalBalance += pre.totalBalance
            cur.totalWithin10days += pre.totalWithin10days
            cur.totalDays31_60 += pre.totalDays31_60
            cur.totalDays61_90 += pre.totalDays61_90
            cur.totalDays91_120 += pre.totalDays91_120
            cur.totalDaysOver120 += pre.totalDaysOver120
            cur.numberOfInvoices += 1
            return true
          }
          return false
        })
        return merged
      },
      companyTotals (companyItems) {
        return [{
          totalWithin10days: companyItems[0].totalWithin10days,
          totalDays31_60: companyItems[0].totalDays31_60,
          totalDays61_90: companyItems[0].totalDays61_90,
          totalDays91_120: companyItems[0].totalDays91_120,
          totalDaysOver120: companyItems[0].totalDaysOver120,
          totalAmount: companyItems[0].totalAmount,
          totalBalance: companyItems[0].totalBalance,
        }]
      },
      onAllClicked () {
        if (this.allChecks) {
          this.lateDays = this.lateDaysAll
        } else {
          this.lateDays = this.lateDaysEmpty
        }
      },
      pieClicked (event, chartContext, config) {
        const info = this.getSelectionInfoFromPie(chartContext, config)
        if (info.found === true) {
          this.companyInvoicesColor = info.color
          const fd = this.filterResults(this.transposedItemData)
          const filterByCo = fd.filter(item => {
            return item.companyName === info.companyName
          })
          this.companyInvoiceItems = filterByCo.sort((a, b) => (a.invoiceAge > b.invoiceAge ? -1 : 1))
          this.fireSelectedCompanyChanged({
            color: info.color,
            invoiceItems: filterByCo.sort((a, b) => (a.invoiceAge > b.invoiceAge ? -1 : 1)),
            company: info.companyName,
            identifier: this.companyInvoiceItems[0].companyId,
            info: this.companyTotals(this.companyInvoiceItems),
          })
        } else {
          this.companyInvoiceItems = []
          this.fireClearSelectedCompany()
        }
      },
      getSelectionInfoFromPie (chartContext, config) {
        if (config.selectedDataPoints[0].length > 0) {
          const companyName = this.allCompanyInvoices[config.dataPointIndex]
            .companyName
          const color =
            chartContext.series.w.globals.colors[config.dataPointIndex]
          return { found: true, companyName, color }
        }
        return { found: false }
      },
      rangeSliderEnd () {
        this.fireClearSelectedCompany()
        const fd = this.filterRange(this.pieInputData)
        this.allCompanyInvoices = fd
        this.pieData = allInvoicesPieData(fd, this.getTheme())
        this.showTotals = true
      },
      sumSelectedInvoices () {
        const sum = this.allCompanyInvoices.reduce(
          (a, b) => a + (b.totalBalance || 0),
          0,
        )
        console.log('Selected Invoices: ', sum)
        return sum
      },
      sumField (array, key) {
        // sum data in give key (property)
        return array.reduce((a, b) => a + (b[key] || 0), 0)
      },
      countField (array, key) {
        const trueItems = array.filter(item => {
          return item[key] === true
        })
        return trueItems.length
      },
      toggleMaxArea (section) {
        var fullSize = false
        var cl = section.$el.classList
        if (cl.contains('maxArea')) {
          cl.remove('maxArea')
        } else {
          fullSize = true
          cl.add('maxArea')
        }
        return fullSize
      },
      getTheme () {
        return this.$vuetify.theme.isDark ? 'dark' : 'light'
      },
      formatDate (d) {
        return formatDate(d)
      },
      formatNumber (number) {
        return formatNumber(number)
      },
      formatCurrency (currency) {
        return formatCurrency(currency)
      },
      formatPercentage (percentage) {
        return formatPercentage(percentage)
      },
      formatCurrencyBasic (currency) {
        return formatCurrencyBasic(currency)
      },
    },
  }
</script>

<style scoped>
